/**
 * Body-text builders for Echo system comments. Each function returns a
 * `SystemMessage` containing the event-type key and the body string that gets
 * stored as `Comment.body`. The actor's name is *not* included — it is rendered
 * separately from `Comment.userId` in CommentsTab.
 *
 * The `eventKey` is sent on the wire as `kind: "system:${eventKey}"` so the UI
 * (and future filters / per-event rendering) can distinguish activity types
 * without parsing the body text.
 */

export interface SystemMessage {
  /** Goes onto the wire as `system:${eventKey}`. */
  eventKey: string;
  body: string;
}

const formatList = (items: string[]): string => items.join(', ');

export const labelsAddedBody = (added: string[]): SystemMessage => ({
  eventKey: 'labels-added',
  body: `added ${added.length === 1 ? 'label' : 'labels'}: ${formatList(added)}`,
});

export const labelsRemovedBody = (removed: string[]): SystemMessage => ({
  eventKey: 'labels-removed',
  body: `removed ${removed.length === 1 ? 'label' : 'labels'}: ${formatList(removed)}`,
});

export const captionChangedBody = (prev: string, next: string): SystemMessage => ({
  eventKey: 'caption-changed',
  body: prev
    ? `renamed “${prev}” to “${next}”`
    : `set the name to “${next}”`,
});

export const workflowStatusChangedBody = (statusName: string | null): SystemMessage => ({
  eventKey: 'workflow-status-changed',
  body: statusName
    ? `changed workflow status to ${statusName}`
    : 'cleared the workflow status',
});

export const publicLinkCreatedBody = (): SystemMessage => ({
  eventKey: 'public-link-created',
  body: 'created a public link',
});

export const versionUploadedBody = (): SystemMessage => ({
  eventKey: 'version-uploaded',
  body: 'uploaded a new version',
});

export const movedToBinBody = (): SystemMessage => ({
  eventKey: 'moved-to-bin',
  body: 'moved this asset to the bin',
});

export const restoredFromBinBody = (): SystemMessage => ({
  eventKey: 'restored-from-bin',
  body: 'restored this asset from the bin',
});

/** Whether a comment kind represents a system activity row. Accepts legacy `'system'` (no colon). */
export const isSystemKind = (kind: string | undefined): boolean =>
  kind === 'system' || (typeof kind === 'string' && kind.startsWith('system:'));

/** Returns the event key from `system:foo` → `foo`. Returns null for `user` or legacy `'system'`. */
export const getSystemEventKey = (kind: string | undefined): string | null => {
  if (typeof kind !== 'string' || !kind.startsWith('system:')) return null;
  return kind.slice('system:'.length) || null;
};
